import { useCallback, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useSelectedVersion } from '@client/store/ui';
import { useProjectState } from '@client/store/project';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import { requestScrollToElement } from '@features/proposals/ElementListRow/element-scroll-request.store';
import { hasImportableExtension } from './csv-import.helpers';
import { useElementImportsStore } from './element-imports.store';
import { setCsvImportFileExpanded } from './import-list-expand.helpers';
import {
  buildFailedImportMessage,
  useAutoImportCandidates,
} from './use-auto-import.hook';
import { IMPORT_REPLACE_NEEDS_PROPOSAL_MESSAGE } from '@shared/helpers/import-replace.helpers';

/** Client-side pre-flight cap; the server enforces 20 MB. */
const MAX_CSV_BYTES = 10 * 1024 * 1024;

type UseImportKalkylFileOptions = {
  onImported?: (importId: string) => void;
};

type UseImportKalkylFileResult = {
  /** Upload + materialize a kalkyl file; no-ops when another import is in flight. */
  importFile: (file: File) => Promise<void>;
  /** True while an upload/materialize is running. */
  isImporting: boolean;
  /** Toast for a file that fails extension checks. */
  reportInvalidFile: (file: File) => void;
};

/**
 * Shared CSV/XLSX import path for window drop: upload, auto-materialize,
 * snackbars. Queues the park-scroll and opens the file *before*
 * {@link applyChanges} so the new ImportSection can consume the request on
 * mount — {@link onImported} runs too late.
 */
export const useImportKalkylFile = ({
  onImported,
}: UseImportKalkylFileOptions = {}): UseImportKalkylFileResult => {
  const { enqueueSnackbar } = useSnackbar();
  const version = useSelectedVersion();
  const { project } = useProjectState('project');
  const applyChanges = useApplyProjectChanges();
  const { buildImportChanges } = useAutoImportCandidates();
  const [isImporting, setIsImporting] = useState(false);
  const importingRef = useRef(false);

  const reportInvalidFile = useCallback(
    (file: File) => {
      enqueueSnackbar(
        `Only .csv and .xlsx files are supported (received "${file.name}")`,
        { variant: 'error' },
      );
    },
    [enqueueSnackbar],
  );

  const importFile = useCallback(
    async (file: File) => {
      if (importingRef.current) return;
      if (!hasImportableExtension(file.name)) {
        reportInvalidFile(file);
        return;
      }
      if (file.size > MAX_CSV_BYTES) {
        enqueueSnackbar('File is too large (max 10 MB)', { variant: 'error' });
        return;
      }
      if (!project || !version) {
        enqueueSnackbar('No project open', { variant: 'error' });
        return;
      }

      importingRef.current = true;
      setIsImporting(true);
      try {
        const result = await useElementImportsStore
          .getState()
          .uploadCsv(project.id, file);
        if (result.rows.length === 0) {
          enqueueSnackbar('No rows could be read from the file', {
            variant: 'error',
          });
          return;
        }
        const { changes, failedRows, needsProposal } = buildImportChanges(
          result.rows,
          'auto',
          result.import.id,
        );
        const failedMessage = buildFailedImportMessage(failedRows);
        if (failedMessage) {
          enqueueSnackbar(failedMessage, { variant: 'error' });
        }
        if (needsProposal) {
          enqueueSnackbar(IMPORT_REPLACE_NEEDS_PROPOSAL_MESSAGE, {
            variant: 'warning',
          });
          const errorCount = result.errors.length;
          if (errorCount > 0) {
            enqueueSnackbar(`${errorCount} error(s) while reading the file`, {
              variant: 'warning',
            });
          }
          return;
        }
        const ready =
          useElementImportsStore.getState().byKey[String(project.id)];
        const importIds =
          ready?.status === 'ready'
            ? Object.keys(ready.data.importsById)
            : [result.import.id];
        // Must run before applyChanges: that render mounts the new ImportSection
        // and consumeScrollRequest only succeeds if the request is already set.
        requestScrollToElement(result.import.id, { expandAfterScroll: true });
        setCsvImportFileExpanded(importIds, result.import.id, true);
        await applyChanges(changes);
        onImported?.(result.import.id);
        const errorCount = result.errors.length;
        if (errorCount > 0) {
          enqueueSnackbar(
            `Imported ${result.rows.length} rows, ${errorCount} error(s)`,
            { variant: 'warning' },
          );
        } else {
          enqueueSnackbar(`Imported ${result.rows.length} rows`, {
            variant: 'success',
          });
        }
      } catch {
        enqueueSnackbar('Import failed', { variant: 'error' });
      } finally {
        importingRef.current = false;
        setIsImporting(false);
      }
    },
    [
      reportInvalidFile,
      enqueueSnackbar,
      project,
      version,
      applyChanges,
      buildImportChanges,
      onImported,
    ],
  );

  return { importFile, isImporting, reportInvalidFile };
};
